import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { api } from '@/lib/api';
import Avatar from '@/components/base/Avatar';

const splitKeywords = (value?: string | null) =>
  String(value || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const joinKeywords = (list: string[]) => list.filter(Boolean).join(',');

interface AvatarUploadProps {
  value?: string;
  onChange: (url: string) => void;
}

export const AvatarUpload = ({ value, onChange }: AvatarUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await api.uploadAuthImage(file);
      onChange(url);
    } catch (e) {
      const message = e instanceof Error ? e.message : '上傳失敗';
      window.alert(message);
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
      <div className="flex items-center gap-3">
        <Avatar src={value} alt="avatar" className="h-16 w-16 rounded-2xl border border-white shadow-sm" />
        <div className="space-y-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(event) => void handleFileChange(event)} />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
          >
            {uploading ? '上傳中...' : '上傳頭像'}
          </button>
          <p className="text-[11px] text-slate-500">支援 JPG / PNG，將自動更新頭像</p>
        </div>
      </div>
    </div>
  );
};

interface TagInputProps {
  value?: string;
  onChange: (next: string) => void;
  placeholder?: string;
}

export const TagInput = ({ value, onChange, placeholder }: TagInputProps) => {
  const [input, setInput] = useState('');
  const tags = useMemo(() => splitKeywords(value), [value]);

  const appendTag = (raw: string) => {
    const next = raw.trim();
    if (!next) return;
    const exists = tags.some((item) => item.toLowerCase() === next.toLowerCase());
    if (exists) {
      setInput('');
      return;
    }
    onChange(joinKeywords([...tags, next]));
    setInput('');
  };

  const removeTag = (tag: string) => {
    onChange(joinKeywords(tags.filter((item) => item !== tag)));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
      <div className="mb-2 flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-700">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="text-slate-400 hover:text-rose-500">
              <i className="ri-close-line" />
            </button>
          </span>
        ))}
      </div>
      <input
        value={input}
        onChange={(event) => setInput(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            appendTag(input);
          }
        }}
        onBlur={() => appendTag(input)}
        placeholder={placeholder || '輸入後按 Enter 新增'}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-sky-400"
      />
    </div>
  );
};

interface LanguageMultiSelectProps {
  value?: string;
  onChange: (next: string) => void;
}

const LANGUAGE_OPTIONS = ['中文', 'English'];

export const LanguageMultiSelect = ({ value, onChange }: LanguageMultiSelectProps) => {
  const selected = useMemo(() => splitKeywords(value), [value]);
  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(joinKeywords(selected.filter((item) => item !== option)));
      return;
    }
    onChange(joinKeywords([...selected, option]));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
      <div className="flex flex-wrap gap-2">
        {LANGUAGE_OPTIONS.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                active ? 'border-cyan-400 bg-cyan-50 text-cyan-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">支援多選：中文、English</p>
    </div>
  );
};

interface RichTextEditorProps {
  value?: string;
  onChange: (next: string) => void;
}

export const RichTextEditor = ({ value, onChange }: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, param?: string) => {
    document.execCommand(command, false, param);
    onChange(editorRef.current?.innerHTML || '');
  };

  return (
    <div className={`rounded-2xl border bg-white overflow-hidden ${focused ? 'border-cyan-400 shadow-[0_0_0_3px_rgba(34,211,238,0.12)]' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <button type="button" onClick={() => exec('bold')} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">B</button>
        <button type="button" onClick={() => exec('italic')} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white"><i>I</i></button>
        <button type="button" onClick={() => exec('insertUnorderedList')} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white"><i className="ri-list-unordered" /></button>
        <button
          type="button"
          onClick={() => {
            const link = window.prompt('請輸入連結');
            if (link) {
              exec('createLink', link);
            }
          }}
          className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white"
        >
          <i className="ri-link" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="min-h-[140px] px-3 py-2 text-sm text-slate-700 outline-none"
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          onChange(editorRef.current?.innerHTML || '');
        }}
      />
    </div>
  );
};
